import type { NavigationGuard } from "vue-router";

import { whenFeatureFlagsReady } from "@/services/analytics/featureFlags";
import { useAuthStore } from "@/stores/auth";

import { firstEnabledPortalSection, PORTAL_HOME } from "./portalSections";
import canAccessRoute from "./utils/canAccessRoute";

/** Navigation guard for authentication */
export const navigationGuard: NavigationGuard = async (to, from, next) => {
  /** 1. Public routes — no checks needed */
  if (to.meta.public) {
    next();
    return;
  }

  const authStore = useAuthStore();

  /**
   * Wait for any in-flight auth initialization (/auth/me) to settle before
   * gating. Without this, the guard's initial run can decide on a partial
   * localStorage payload — missing termsAcceptedAt, and on the login path
   * also roles, which breaks the superadmin carve-out — and wrongly bounce
   * an already-accepted user to /terms (RAP-350). After first load this is
   * a no-op. We fail closed: if /auth/me cannot confirm the user, the gate
   * still applies — terms is a consent requirement, not a UX nicety.
   */
  await authStore.whenReady();

  /** 2. Auth check — all non-public routes require authentication */
  if (!authStore.isAuthenticated) {
    const query: { redirect?: string } = {};
    if (to.fullPath !== "/") {
      query.redirect = to.fullPath;
    }
    next({
      name: "SignIn",
      query: Object.keys(query).length > 0 ? query : undefined,
    });
    return;
  }

  /**
   * 3. Terms acceptance — must come before access checks.
   *
   * Hardcoded "AcceptTerms" route name is ok for now as it's the only authenticated route that
   * bypasses terms acceptance. If more pages need to bypass in the future, refactor to use meta.
   */
  if (to.name !== "AcceptTerms" && authStore.needsToAcceptTerms) {
    next({ name: "AcceptTerms", query: { redirect: to.fullPath } });
    return;
  }

  /**
   * 3b. Portal users are gated per section by Mixpanel flags, which resolve asynchronously after
   * identify. Judging a reload on /portal/capital against the still-empty cache would bounce the
   * user off a page they can see, so wait for the first answer (bounded — Mixpanel being down
   * must not hang navigation; it then fails closed like any unknown flag).
   */
  if (authStore.isReferringPortalEnabled) {
    await whenFeatureFlagsReady();
  }

  /** 4. Access check — delegates to each route's canAccess predicate via the matched chain */
  if (!canAccessRoute(to.matched)) {
    /**
     * Where a denied user lands. Dashboard for everyone EXCEPT an audit-only account, which
     * cannot access the Dashboard either (RAP-588) — sending it there produced the 403 error
     * card that account used to land on after login, and as a guard fallback it would be a
     * redirect the account can never satisfy.
     *
     * The fallback MUST be a route the user can actually reach, or it is a loop rather than a
     * fallback. That stopped being automatic when Data Sharing moved from a role-name gate to
     * `VIEW_DATA_SHARING`: an audit account that has not been granted the permission is denied
     * `/data-sharing` and then sent straight back to it. This is the third variant of that shape
     * on this feature — `/auth/me`, then `/terms`, now the fallback itself — so it is checked
     * rather than reasoned about.
     */
    const auditorTarget = to.name === "DataSharing" ? null : "DataSharing";
    /**
     * A referring-portal user's fallback is their own app's landing page, not Dashboard
     * (RAP-630). Dashboard is precisely one of the routes the portal deny rule closes to them,
     * so sending them there would be the same never-satisfiable redirect the audit account hit
     * — the fourth variant of that shape on this guard, which is why it is checked here rather
     * than assumed. The `to.name` test is the same loop-breaker as the auditor's above.
     *
     * This is also what makes "old routes redirect" true: a flagged firm's user who follows an
     * old bookmark to /campaigns is denied by `canAccessRoute` and lands on Summary.
     *
     * "Summary" is really "the first section this user can open" — each section has its own
     * Mixpanel gate (and Settings a funder rule), so the target is computed. With every section
     * off it is `/portal` itself, which is never gated and renders a "nothing enabled yet" page,
     * so the fallback is always reachable.
     */
    const portalLanding = firstEnabledPortalSection() ?? PORTAL_HOME;
    const portalTarget = to.name === portalLanding ? null : portalLanding;
    let fallback: null | string = "Dashboard";
    if (authStore.isGrantAuditor) {
      fallback = auditorTarget;
    } else if (authStore.isReferringPortalEnabled) {
      fallback = portalTarget;
    }
    if (!fallback) {
      // Nowhere left to send them: the one screen this account exists for is closed to it. Ending
      // the navigation shows the denial where it happened instead of bouncing forever.
      next(false);
      return;
    }
    next({ name: fallback });
    return;
  }

  /** 5. All checks passed */
  next();
};
